import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UzButton, UzCard, UzConfirmDialog, UzSpinner } from '@utazzo/ui';
import { LogOut, Monitor, Smartphone, Tablet } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { listMySessions, signOutOtherSessions, signOutSession } from '../sessions.api';

import type { UserSession } from '@utazzo/shared';

import { relativeTime } from '@/features/dashboard/dashboard-utils';
import { extractServerError } from '@/lib/api-error';
import { parseUserAgent, type DeviceKind } from '@/lib/device';

const SESSIONS_KEY = ['me', 'sessions'] as const;

const KIND_ICON: Record<DeviceKind, typeof Smartphone> = {
  phone: Smartphone,
  tablet: Tablet,
  desktop: Monitor,
};

let regionNames: Intl.DisplayNames | null | undefined;
/** "IN" -> "India"; falls back to the code where Intl.DisplayNames is missing. */
const countryName = (code: string): string => {
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      regionNames = null;
    }
  }
  try {
    return regionNames?.of(code) ?? code;
  } catch {
    return code;
  }
};

/** Rows on first open, then how many each "View more" adds. */
const FIRST_PAGE = 6;
const MORE_PAGE = 10;

/** ::1 / 127.x - signed in through a local dev server, not a real network. */
const isLoopback = (ip: string): boolean => ip === '::1' || /^127\./.test(ip);

/** "Mumbai, India" / "India" / "Local dev" / the IP / null. */
const whereLabel = (s: UserSession): string | null => {
  const city = s.location?.city;
  const country = s.location?.country ? countryName(s.location.country) : null;
  if (city && country) return `${city}, ${country}`;
  const place = country ?? city;
  if (place) return place;
  if (s.ipAddress && isLoopback(s.ipAddress)) return 'Local dev';
  return s.ipAddress;
};

/** "Just now" -> "just now", "Yesterday" -> "yesterday"; "5m ago" / "30 Sep" as is. */
const activeAgo = (iso: string): string => {
  const rel = relativeTime(iso);
  return /^[A-Z][a-z]/.test(rel) ? rel.charAt(0).toLowerCase() + rel.slice(1) : rel;
};

const errorText = (err: unknown, fallback: string): string =>
  extractServerError(err)?.message ?? fallback;

/**
 * "Where you're signed in" (My profile): every device signed in to this
 * account, like Instagram's login activity. Signing one out is immediate -
 * its next request is refused and it lands on the login page.
 */
export const ActiveSessionsCard = (): React.JSX.Element => {
  const qc = useQueryClient();
  const sessions = useQuery({ queryKey: SESSIONS_KEY, queryFn: listMySessions, retry: false });
  const [confirmAll, setConfirmAll] = useState(false);
  const [shown, setShown] = useState(FIRST_PAGE);

  const signOutOne = useMutation({
    mutationFn: signOutSession,
    onSuccess: () => toast.success('Signed out of that device.'),
    onError: (err) => toast.error(errorText(err, 'Could not sign that device out.')),
    onSettled: () => void qc.invalidateQueries({ queryKey: SESSIONS_KEY }),
  });

  const signOutAll = useMutation({
    mutationFn: signOutOtherSessions,
    onSuccess: (r) =>
      toast.success(
        r.revoked === 0
          ? 'No other devices were signed in.'
          : `Signed out of ${r.revoked} other device${r.revoked === 1 ? '' : 's'}.`,
      ),
    onError: (err) => toast.error(errorText(err, 'Could not sign the other devices out.')),
    onSettled: () => void qc.invalidateQueries({ queryKey: SESSIONS_KEY }),
  });

  const list = sessions.data ?? [];
  const others = list.filter((s) => !s.current);
  // The API sorts this device first, so it is always on the first page.
  const visible = list.slice(0, shown);

  return (
    <UzCard
      title="Where you're signed in"
      description="Every device signed in to your account. Sign out any you don't recognise - it's signed out immediately."
    >
      {sessions.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <UzSpinner size="sm" /> Loading your devices...
        </div>
      ) : sessions.isError ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-500">
          Could not load your devices.
          <UzButton variant="ghost" size="sm" onClick={() => void sessions.refetch()}>
            Try again
          </UzButton>
        </div>
      ) : (
        <div className="space-y-4">
          <ul className="divide-y divide-zinc-100 rounded-md border border-zinc-200">
            {visible.map((s) => {
              const { os, browser, kind } = parseUserAgent(s.userAgent ?? '');
              const Icon = KIND_ICON[kind];
              const where = whereLabel(s);
              const signingOut = signOutOne.isPending && signOutOne.variables === s.id;
              return (
                <li key={s.id} className="flex items-center gap-3 px-3 py-2.5">
                  <Icon className="h-5 w-5 shrink-0 text-zinc-400" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-zinc-800">
                      {browser ? `${os} · ${browser}` : os}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {where && <span>{where} · </span>}
                      {s.current ? (
                        <span className="font-medium text-emerald-700">This device</span>
                      ) : (
                        <span title={new Date(s.lastActiveAt).toLocaleString('en-GB')}>
                          Active {activeAgo(s.lastActiveAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  {!s.current && (
                    <UzButton
                      variant="ghost"
                      size="sm"
                      aria-label={`Sign out ${os}${browser ? ` ${browser}` : ''}`}
                      onClick={() => signOutOne.mutate(s.id)}
                      loading={signingOut}
                      disabled={signOutOne.isPending || signOutAll.isPending}
                    >
                      Sign out
                    </UzButton>
                  )}
                </li>
              );
            })}
          </ul>

          {list.length > visible.length && (
            <div className="flex justify-center">
              <UzButton variant="ghost" size="sm" onClick={() => setShown((n) => n + MORE_PAGE)}>
                View more
              </UzButton>
            </div>
          )}

          {others.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-zinc-500">
                Phone notifications keep reaching a signed-out phone until you also remove it under
                Phone notifications → Your devices.
              </p>
              <UzButton
                variant="outline"
                size="sm"
                onClick={() => setConfirmAll(true)}
                disabled={signOutAll.isPending || signOutOne.isPending}
              >
                <LogOut className="mr-1.5 h-4 w-4" />
                Sign out all other devices
              </UzButton>
            </div>
          ) : (
            <p className="text-xs text-zinc-500">You're only signed in on this device.</p>
          )}
        </div>
      )}

      <UzConfirmDialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title="Sign out of all other devices?"
        description={`${others.length} other device${others.length === 1 ? '' : 's'} will be signed out right away. You stay signed in here.`}
        confirmLabel="Sign them out"
        onConfirm={async () => {
          await signOutAll.mutateAsync().catch(() => undefined);
        }}
      />
    </UzCard>
  );
};
